import { useEffect, useState, useRef } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ArrowLeft, Play, Pause, Volume2, VolumeX, Maximize,
  Clock, Calendar, Film, Share2, Heart, BarChart2, Scissors
} from "lucide-react";
import { Button } from "./ui/button";
import YouTubePlayer from "./YouTubePlayer";
import ClipCreator from "./ClipCreator";
import { PlayByPlayWidget } from "./PlayByPlayWidget";
import axios from "axios";
import { updateWatchProgress } from "../services/watchHistory";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const RecordingPlayerPage = () => {
  const { recordingId } = useParams();
  const navigate = useNavigate();
  const [recording, setRecording] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedRecordings, setRelatedRecordings] = useState([]);
  const [showClipCreator, setShowClipCreator] = useState(false);
  const [currentVideoTime, setCurrentVideoTime] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);
  const [favoriteLoading, setFavoriteLoading] = useState(false);
  const progressTrackerRef = useRef(null);
  const playerRef = useRef(null);

  // Check if user is logged in
  const isLoggedIn = () => {
    return localStorage.getItem('user_token') !== null;
  };

  // Get auth headers for user requests
  const getUserAuthHeader = () => {
    const token = localStorage.getItem('user_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    const fetchRecording = async () => {
      try {
        // Fetch from public on-demand endpoint
        const response = await axios.get(`${API}/recordings/on-demand/${recordingId}`);
        setRecording(response.data);

        // Fetch related recordings by same sport
        if (response.data.sport) {
          const relatedRes = await axios.get(`${API}/recordings/on-demand?sport=${response.data.sport}&limit=4`);
          setRelatedRecordings(relatedRes.data.filter(r => r.id !== recordingId).slice(0, 3));
        }

        // Check if this recording is favorited by the user
        if (isLoggedIn()) {
          try {
            const favRes = await axios.get(`${API}/users/favorites`, { headers: getUserAuthHeader() });
            const favorites = favRes.data.favorites || [];
            setIsFavorite(favorites.some(f => f.id === recordingId));
          } catch (err) {
            // Not logged in or error, ignore
          }
        }
      } catch (error) {
        console.error("Error fetching recording:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchRecording();
  }, [recordingId]);

  // Toggle favorite status
  const toggleFavorite = async () => {
    if (!isLoggedIn()) {
      // Redirect to login
      navigate('/login-user?redirect=' + encodeURIComponent(window.location.pathname));
      return;
    }

    setFavoriteLoading(true);
    try {
      if (isFavorite) {
        await axios.delete(`${API}/users/favorites/${recordingId}`, { headers: getUserAuthHeader() });
        setIsFavorite(false);
      } else {
        await axios.post(`${API}/users/favorites`, { recording_id: recordingId }, { headers: getUserAuthHeader() });
        setIsFavorite(true);
      }
    } catch (error) {
      console.error("Error toggling favorite:", error);
    } finally {
      setFavoriteLoading(false);
    }
  };

  // Track watch progress
  useEffect(() => {
    if (!recording) return;

    // Initial tracking when video loads
    updateWatchProgress({
      content_type: 'recording',
      content_id: recordingId,
      title: recording.title,
      thumbnail: recording.thumbnail_url,
      duration: recording.duration || 0,
      progress: 0
    });

    // Set up periodic progress tracking (every 10 seconds)
    const trackProgress = () => {
      // Try to get progress from YouTube player or video element
      const iframe = document.querySelector('iframe[src*="youtube"]');
      const video = document.querySelector('video');
      
      let progress = 0;
      let duration = recording.duration || 0;
      
      if (video) {
        progress = video.currentTime;
        duration = video.duration || duration;
      }
      
      if (progress > 0) {
        updateWatchProgress({
          content_type: 'recording',
          content_id: recordingId,
          title: recording.title,
          thumbnail: recording.thumbnail_url,
          duration: Math.floor(duration),
          progress: Math.floor(progress)
        });
      }
    };

    progressTrackerRef.current = setInterval(trackProgress, 10000);

    return () => {
      if (progressTrackerRef.current) {
        clearInterval(progressTrackerRef.current);
      }
    };
  }, [recording, recordingId]);

  const formatDuration = (seconds) => {
    if (!seconds) return "00:00";
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hours > 0) {
      return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!recording) {
    return (
      <div className="min-h-screen bg-[#050505] flex flex-col items-center justify-center">
        <Film className="w-16 h-16 text-zinc-700 mb-4" />
        <h1 className="font-secondary text-2xl font-bold uppercase text-white mb-2">Recording Not Found</h1>
        <p className="text-zinc-500 mb-6">This recording may have been removed or is unavailable</p>
        <Link to="/watch">
          <Button className="bg-white text-black hover:bg-zinc-200 font-secondary font-bold uppercase tracking-widest text-xs px-6 py-3 h-auto rounded-none">
            Browse Watch
          </Button>
        </Link>
      </div>
    );
  }

  // Check if recording has YouTube URL or direct video URL
  const hasYouTube = recording.youtube_url && recording.youtube_url.trim() !== "";
  const hasDirectVideo = recording.video_url && recording.video_url.trim() !== "";
  const hasHLS = recording.hls_url && recording.hls_url.trim() !== "";
  const hasPlayableContent = hasYouTube || hasDirectVideo || hasHLS;

  return (
    <main data-testid="recording-player-page" className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="bg-black border-b border-white/10 sticky top-0 z-40">
        <div className="max-w-[1600px] mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/watch" className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors">
                <ArrowLeft className="w-5 h-5" />
                <span className="text-sm hidden sm:inline">Back to Watch</span>
              </Link>
            </div>
            <div className="flex items-center gap-3">
              <Button 
                variant="ghost" 
                size="icon"
                className="text-zinc-400 hover:text-white"
              >
                <Share2 className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-[1200px] mx-auto">
        {/* Video Player - Reduced size */}
        <div className="relative bg-black mx-auto max-w-5xl">
          {hasYouTube ? (
            /* YouTube Player - Custom ASN Interface */
            <YouTubePlayer
              youtubeUrl={recording.youtube_url}
              isLive={false}
              autoPlay={false}
              showClipButton={true}
              onClipCreate={(time) => setShowClipCreator(true)}
            />
          ) : hasDirectVideo || hasHLS ? (
            /* Direct Video Player (MP4/HLS) */
            <div className="relative aspect-video bg-black">
              <video
                ref={playerRef}
                className="w-full h-full"
                controls
                poster={recording.thumbnail_url}
                preload="metadata"
                onTimeUpdate={(e) => setCurrentVideoTime(e.target.currentTime)}
                onLoadedMetadata={(e) => setVideoDuration(e.target.duration)}
              >
                {hasHLS && <source src={recording.hls_url} type="application/x-mpegURL" />}
                {hasDirectVideo && <source src={recording.video_url} type="video/mp4" />}
                Your browser does not support the video tag.
              </video>
            </div>
          ) : (
            /* Fallback - Thumbnail with play overlay */
            <div className="relative aspect-video">
              {recording.thumbnail_url ? (
                <img
                  src={recording.thumbnail_url}
                  alt={recording.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-zinc-900">
                  <Film className="w-24 h-24 text-zinc-700" />
                </div>
              )}

              {/* Play Overlay */}
              <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                <div className="text-center">
                  <motion.div 
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    className="w-24 h-24 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mb-4 mx-auto"
                  >
                    <Play className="w-10 h-10 text-white ml-1" fill="white" />
                  </motion.div>
                  <p className="text-zinc-400 text-sm">Video source not configured</p>
                  <p className="text-zinc-600 text-xs mt-1">Admin: Add video URL to enable playback</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="px-4 md:px-8 py-8 max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              
              {/* Team Matchup Banner */}
              {(recording.home_team || recording.away_team) && (
                <div className="relative overflow-hidden rounded-lg">
                  {/* Gradient Background with Team Colors */}
                  <div 
                    className="absolute inset-0"
                    style={{
                      background: `linear-gradient(135deg, ${recording.away_team?.color || '#1a1a1a'} 0%, ${recording.away_team?.color || '#1a1a1a'} 45%, ${recording.home_team?.color || '#003087'} 55%, ${recording.home_team?.color || '#003087'} 100%)`
                    }}
                  />
                  <div className="absolute inset-0 bg-black/40" />
                  
                  {/* Final Badge - positioned at top */}
                  {(recording.final_home_score !== null || recording.final_away_score !== null) && (
                    <div className="absolute top-2 left-1/2 -translate-x-1/2 bg-black/60 backdrop-blur-sm px-3 py-1 rounded-full z-10 flex items-center justify-center">
                      <span className="text-white text-[10px] font-bold uppercase tracking-widest leading-none">Final</span>
                    </div>
                  )}
                  
                  <div className="relative flex items-center justify-between px-4 pt-8 pb-4 md:px-6 md:pt-9 md:pb-5">
                    {/* Away Team */}
                    <div className="flex items-center gap-3 flex-1">
                      {recording.away_team?.logo_url ? (
                        <img 
                          src={recording.away_team.logo_url} 
                          alt={recording.away_team.name}
                          className="w-10 h-10 md:w-12 md:h-12 object-contain bg-white/10 rounded-md p-1"
                        />
                      ) : (
                        <div 
                          className="w-10 h-10 md:w-12 md:h-12 rounded-md flex items-center justify-center text-sm font-bold text-white"
                          style={{ backgroundColor: recording.away_team?.color || '#333' }}
                        >
                          {recording.away_team?.abbreviation?.slice(0, 3) || recording.away_team?.name?.slice(0, 3) || 'AWY'}
                        </div>
                      )}
                      <div className="text-left">
                        <h3 className="text-white font-bold text-sm md:text-base uppercase">
                          {recording.away_team?.abbreviation || recording.away_team?.name || 'Away'}
                        </h3>
                      </div>
                    </div>
                    
                    {/* Score */}
                    <div className="flex items-center gap-2 md:gap-4 px-3">
                      <span className="text-2xl md:text-4xl font-bold text-white tabular-nums">
                        {recording.final_away_score ?? '-'}
                      </span>
                      <span className="text-lg md:text-xl font-bold text-white/50">-</span>
                      <span className="text-2xl md:text-4xl font-bold text-white tabular-nums">
                        {recording.final_home_score ?? '-'}
                      </span>
                    </div>
                    
                    {/* Home Team */}
                    <div className="flex items-center gap-3 flex-1 justify-end">
                      <div className="text-right">
                        <h3 className="text-white font-bold text-sm md:text-base uppercase">
                          {recording.home_team?.abbreviation || recording.home_team?.name || 'Home'}
                        </h3>
                      </div>
                      {recording.home_team?.logo_url ? (
                        <img 
                          src={recording.home_team.logo_url} 
                          alt={recording.home_team.name}
                          className="w-10 h-10 md:w-12 md:h-12 object-contain bg-white/10 rounded-md p-1"
                        />
                      ) : (
                        <div 
                          className="w-10 h-10 md:w-12 md:h-12 rounded-md flex items-center justify-center text-sm font-bold text-white"
                          style={{ backgroundColor: recording.home_team?.color || '#333' }}
                        >
                          {recording.home_team?.abbreviation?.slice(0, 3) || recording.home_team?.name?.slice(0, 3) || 'HME'}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Title & Meta */}
              <div>
                {recording.sport && (
                  <span className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2 block">
                    {recording.sport}
                  </span>
                )}
                <h1 className="font-secondary text-2xl md:text-3xl font-bold uppercase tracking-tight text-white">
                  {recording.title}
                </h1>
                
                <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-zinc-500">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {formatDate(recording.recorded_at)}
                  </span>
                  {recording.duration_seconds > 0 && (
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {formatDuration(recording.duration_seconds)}
                    </span>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-3 py-4 border-t border-b border-white/10">
                <Button 
                  variant="ghost"
                  onClick={toggleFavorite}
                  disabled={favoriteLoading}
                  className={`flex items-center gap-2 ${isFavorite ? 'text-red-500 hover:text-red-400' : 'text-zinc-400 hover:text-red-500'}`}
                  data-testid="favorite-btn"
                >
                  <Heart className={`w-5 h-5 ${isFavorite ? 'fill-current' : ''}`} />
                  {isFavorite ? 'Favorited' : 'Favorite'}
                </Button>
                <Button 
                  variant="ghost"
                  className="flex items-center gap-2 text-zinc-400 hover:text-white"
                >
                  <Share2 className="w-5 h-5" />
                  Share
                </Button>
                <Button 
                  variant="ghost"
                  onClick={() => setShowClipCreator(true)}
                  className="flex items-center gap-2 text-zinc-400 hover:text-orange-500"
                  data-testid="create-clip-btn"
                >
                  <Scissors className="w-5 h-5" />
                  Create Clip
                </Button>
                {recording.stats_link && (
                  <a 
                    href={recording.stats_link} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-zinc-400 hover:text-white px-4 py-2"
                  >
                    <BarChart2 className="w-5 h-5" />
                    Game Stats
                  </a>
                )}
              </div>

              {/* Play-by-Play Widget - Shows for recordings with stats_link */}
              {recording.stats_link && (
                <PlayByPlayWidget 
                  statsUrl={recording.stats_link}
                  sport={recording.sport?.toLowerCase() || 'basketball'}
                  isLive={false}
                  homeTeam={recording.home_team}
                  awayTeam={recording.away_team}
                  maxPlays={20}
                  autoRefresh={false}
                />
              )}

              {/* Description */}
              {recording.description && (
                <div className="bg-zinc-900/50 border border-white/5 p-4">
                  <h3 className="font-secondary text-sm font-bold uppercase tracking-widest text-zinc-500 mb-3">Description</h3>
                  <p className="text-zinc-300 text-sm leading-relaxed">{recording.description}</p>
                </div>
              )}
            </div>

            {/* Sidebar - Related Recordings */}
            <div className="lg:col-span-1">
              <h3 className="font-secondary text-sm font-bold uppercase tracking-widest text-zinc-500 mb-4">Related Recordings</h3>
              
              {relatedRecordings.length === 0 ? (
                <p className="text-zinc-600 text-sm">No related recordings found</p>
              ) : (
                <div className="space-y-4">
                  {relatedRecordings.map(related => (
                    <Link 
                      key={related.id}
                      to={`/on-demand/${related.id}`}
                      className="flex gap-3 group"
                    >
                      <div className="relative w-40 aspect-video bg-zinc-900 flex-shrink-0 overflow-hidden">
                        {related.thumbnail_url ? (
                          <img
                            src={related.thumbnail_url}
                            alt={related.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Film className="w-6 h-6 text-zinc-700" />
                          </div>
                        )}
                        {related.duration_seconds > 0 && (
                          <div className="absolute bottom-1 right-1 bg-black/80 px-1 py-0.5 text-[10px] text-white font-mono">
                            {formatDuration(related.duration_seconds)}
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-secondary text-xs font-bold uppercase tracking-wide text-white group-hover:text-zinc-300 line-clamp-2">
                          {related.title}
                        </h4>
                        {related.recorded_at && (
                          <p className="text-[10px] text-zinc-500 mt-1">
                            {new Date(related.recorded_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </p>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>
              )}

              {/* Browse All Link */}
              <Link 
                to="/watch"
                className="block mt-6 text-center py-3 border border-white/10 text-zinc-400 hover:text-white hover:border-white/30 transition-colors text-xs font-bold uppercase tracking-widest"
              >
                Browse All Content
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="noise-overlay" aria-hidden="true" />

      {/* Clip Creator Modal */}
      <ClipCreator
        recordingId={recordingId}
        recordingTitle={recording?.title || 'Recording'}
        duration={recording?.duration_seconds || videoDuration}
        currentTime={currentVideoTime}
        onSeek={(time) => {
          // Try to seek the YouTube player
          if (playerRef.current?.seekTo) {
            playerRef.current.seekTo(time, true);
          }
        }}
        isOpen={showClipCreator}
        onClose={() => setShowClipCreator(false)}
      />
    </main>
  );
};

export default RecordingPlayerPage;
